/* The client's phone, from `profile-web-mockups-v2/f-zakladki.html` (`.ip`, `.screen`, `.s` and the
 * blocks under it), transcribed value for value.
 *
 * ── Everything inside the screen is sized in `cqw` ──────────────────────────────────────────
 * `.screen` is a `container-type: inline-size`, so every number inside it is a percentage of the
 * PHONE's width rather than of the page. The whole preview therefore scales with the column: at
 * 1512 it is a 720px phone, at 1280 it is whatever the shorter window leaves, and the type, the
 * radii and the gaps inside it all follow without a second breakpoint. Those fractions are the
 * `--ph-*` group in `tokens/spacing.css` rather than the app's own scale — see the note there: a
 * `--space-1` inside a container query is 4px at every phone size, which is the one thing this
 * surface must not be.
 *
 * ── The colours are the client's app, not the trainer's theme ───────────────────────────────
 * `--phone-*` in `tokens/colors.css`, theme-independent on purpose — see the note beside them. The
 * four `--brand-*` properties are the DRAFT, handed down by the component from
 * `entities/brand-color`'s `brandPaint`, which is why dragging the picker repaints this on the
 * frame it is dragged on.
 *
 * ── The drop under the frame is neutral ─────────────────────────────────────────────────────
 * `--shadow-phone`, black at .22. The coloured-glow ban has exactly one exception in this app and
 * it is the ember primary; a phone standing over a tinted blur would be the second.
 *
 * Every colour, radius and type value is a token; the fractions are layout. */

.column {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: var(--space-4);
  align-items: center;
  justify-content: center;
  min-height: 0;
}

/* The phone's height on this tab; its width follows the frame (`shared/ui/PhoneFrame`). */
.phoneSize {
  height: min(var(--h-account-phone), 100dvh - var(--h-account-phone-reserve));
}

.caption {
  display: flex;
  flex: none;
  gap: var(--space-2);
  align-items: center;
  font-size: var(--fs-foot);
  font-weight: var(--fw-bold);
  color: var(--text-2);
}

.captionIcon {
  display: block;
  flex: none;
  width: var(--sz-account-glyph-md);
  height: var(--sz-account-glyph-md);
}

.body {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  gap: var(--ph-space-7);
  padding: var(--ph-space-14) var(--ph-space-12) var(--space-0);
  font-size: var(--ph-fs-body);
}

.hello {
  font-size: var(--ph-fs-hello);
  font-weight: var(--fw-semibold);
  color: var(--phone-text-2);
}

.today {
  display: block;
  margin-top: var(--ph-space-1);
  font-size: var(--ph-fs-today);
  font-weight: var(--fw-extrabold);
  line-height: var(--lh-plan);
  letter-spacing: var(--ls-home-title);
  color: var(--phone-text);
}

.card {
  padding: var(--ph-space-10);
  background: var(--phone-card);
  border: var(--border-hairline) solid var(--phone-edge);
  border-radius: var(--ph-radius-card);
}

.eyebrow {
  font-size: var(--ph-fs-xs);
  font-weight: var(--fw-bold);
  letter-spacing: var(--ls-badge);
  color: var(--phone-text-3);
}

.cardTitle {
  display: block;
  margin: var(--ph-space-2) var(--space-0) var(--ph-space-6);
  font-size: var(--ph-fs-card);
  font-weight: var(--fw-extrabold);
  letter-spacing: var(--ls-section);
}

/* The last card's title has nothing under it, so it drops the gap the others reserve for a bar. */
.cardTitleLast {
  margin-bottom: var(--space-0);
}

/* The progress bar. Its BOX is here and its fill is the brand's, which is the one row on this
 * screen where the trainer's colour is a measurement rather than a decoration. */
.track {
  display: block;
  height: 2cqw;
  overflow: hidden;
  background: var(--phone-track);
  border-radius: var(--ph-radius-track);
}

.fillBar {
  display: block;
  width: 62%;
  height: 100%;
  background: var(--brand-fill);
  border-radius: var(--ph-radius-track);
}

.meta {
  display: flex;
  justify-content: space-between;
  margin-top: var(--ph-space-4);
  font-size: var(--ph-fs-note);
  font-weight: var(--fw-semibold);
  color: var(--phone-text-2);
}

/* The client's start button — the brand at its loudest, and the reason the fill is ADJUSTED rather
 * than taken raw: `entities/brand-color` moves it until this label clears AA. */
.cta {
  display: grid;
  height: 13cqw;
  margin-top: var(--ph-space-8);
  font-size: var(--ph-fs-cta);
  font-weight: var(--fw-extrabold);
  color: var(--brand-ink);
  place-items: center;
  background: var(--brand-fill);
  border-radius: var(--ph-radius-cta);
}

.coach {
  display: flex;
  gap: var(--ph-space-6);
  align-items: center;
  font-size: var(--ph-fs-coach);
  font-weight: var(--fw-semibold);
  color: var(--phone-text-2);
}

/* The trainer's own disc, ringed in their colour — the SOLID hue rather than the adjusted fill,
 * because a ring carries no words and holds 1.4.11's 3:1 instead of 4.5:1. */
.avatar {
  display: grid;
  flex: none;
  width: 10cqw;
  height: 10cqw;
  font-size: var(--ph-fs-note);
  font-weight: var(--fw-extrabold);
  color: var(--phone-text);
  place-items: center;
  background: var(--phone-track);
  border-radius: var(--r-circle);
  box-shadow: var(--ph-ring-avatar);
}

.coachText {
  min-width: 0;
}

.coachName {
  display: block;
  font-size: var(--ph-fs-name);
  font-weight: var(--fw-bold);
  color: var(--phone-text);
}

/* „Napisz" — the brand as WORDS, so it takes the ink the contrast walk produced for these two
 * surfaces rather than the raw hue, which on a pale brand would be unreadable on white. */
.message {
  display: grid;
  height: 8.4cqw;
  padding: var(--space-0) var(--ph-space-7);
  margin-left: auto;
  font-size: var(--ph-fs-note);
  font-weight: var(--fw-bold);
  color: var(--brand-mark);
  place-items: center;
  border: var(--border-hairline) solid var(--brand-mark);
  border-radius: var(--r-pill);
}

.week {
  display: flex;
  gap: var(--ph-space-3);
  margin-top: var(--ph-space-5);
}

.day {
  display: grid;
  flex: 1;
  height: 8.6cqw;
  font-size: var(--ph-fs-sm);
  font-style: normal;
  font-weight: var(--fw-bold);
  color: var(--phone-text-3);
  place-items: center;
  background: var(--phone-bg);
  border-radius: var(--ph-radius-day);
}

.dayDone {
  color: var(--brand-ink);
  background: var(--brand-fill);
}

.dayNext {
  color: var(--phone-text);
  background: var(--phone-card);
  box-shadow: var(--ph-ring-next);
}

.tabs {
  display: flex;
  align-items: center;
  justify-content: space-around;
  height: 17cqw;
  padding-bottom: var(--ph-space-9);
  margin-top: auto;
  margin-inline: var(--ph-space-gutter-out);
  background: var(--phone-card);
  border-top: var(--border-hairline) solid var(--phone-edge);
}

.tabMark {
  width: 6cqw;
  height: 6cqw;
  background: var(--phone-tab-idle);
  border-radius: var(--ph-radius-mark);
}

.tabMarkOn {
  background: var(--brand-solid);
}
